import { QueryClient, type DefaultOptions } from '@tanstack/react-query';

// Default options for React Query
const defaultOptions: DefaultOptions = {
  queries: {
    // Stale time: 5 minutes
    staleTime: 5 * 60 * 1000,

    // Cache time: 10 minutes
    gcTime: 10 * 60 * 1000,

    // Retry failed requests 3 times with exponential backoff
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Refetch on window focus in production
    refetchOnWindowFocus: import.meta.env.PROD,

    // Don't refetch on mount if data is fresh
    refetchOnMount: false,

    // Don't refetch on reconnect by default
    refetchOnReconnect: false,
  },
  mutations: {
    // Retry mutations once on failure
    retry: 1,
    retryDelay: 1000,
  },
};

// Create and export the query client
export const queryClient = new QueryClient({
  defaultOptions,
});

// Query keys factory for type-safe query keys
export const queryKeys = {
  // Auth
  auth: {
    me: ['auth', 'me'] as const,
  },

  // API Keys
  apiKeys: {
    all: ['apiKeys'] as const,
    list: () => [...queryKeys.apiKeys.all, 'list'] as const,
    detail: (id: string) => [...queryKeys.apiKeys.all, 'detail', id] as const,
  },

  // Usage
  usage: {
    all: ['usage'] as const,
    stats: () => [...queryKeys.usage.all, 'stats'] as const,
    records: (filters?: Record<string, unknown>) =>
      [...queryKeys.usage.all, 'records', filters] as const,
  },

  // Subscription
  subscription: {
    all: ['subscription'] as const,
    current: () => [...queryKeys.subscription.all, 'current'] as const,
    plans: () => [...queryKeys.subscription.all, 'plans'] as const,
  },

  // Stock Data
  stock: {
    all: ['stock'] as const,
    query: (query: string) => [...queryKeys.stock.all, 'query', query] as const,
    search: (keyword: string) => [...queryKeys.stock.all, 'search', keyword] as const,
  },
};
